/**
 * Định dạng số theo trình duyệt (localStorage) — tương tự cài đặt "Định dạng số" bên YTL Logistics.
 * Formatter trong `commonUtils` đọc cache module này mỗi lần render, không cần context.
 */

/** `en`: 1,234.56 — `vi`: 1.234,56 */
export type NumberSeparatorStyle = 'en' | 'vi'

export type NumberDecimalPrefKey = 'vnd' | 'usd' | 'rmb' | 'general' | 'volume'

export type NumberDisplayFormatPrefs = Record<NumberDecimalPrefKey, number> & {
  separator: NumberSeparatorStyle
}

export const NUMBER_DECIMAL_PREF_KEYS: NumberDecimalPrefKey[] = ['vnd', 'usd', 'rmb', 'general', 'volume']

export const DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS: NumberDisplayFormatPrefs = {
  vnd: 0,
  usd: 6,
  rmb: 6,
  general: 6,
  volume: 6,
  separator: 'en',
}

/** Mã loại tiền (`commonConstants` RMB/USD/VND) → khoá cài đặt số thập phân. */
const CURRENCY_PREF_KEY: Record<string, NumberDecimalPrefKey> = {
  VND: 'vnd',
  USD: 'usd',
  RMB: 'rmb',
  CNY: 'rmb',
  NDT: 'rmb',
}

export function currencyPrefKey(currency: unknown): NumberDecimalPrefKey {
  return CURRENCY_PREF_KEY[String(currency ?? '').trim().toUpperCase()] ?? 'general'
}

export const NUMBER_DISPLAY_FORMAT_STORAGE_KEY = 'muahang-number-display-format-v1'
export const NUMBER_DISPLAY_FORMAT_CHANGE_EVENT = 'muahang-number-display-format-change'
export const NUMBER_DECIMALS_MIN = 0
export const NUMBER_DECIMALS_MAX = 9

let cache: NumberDisplayFormatPrefs | null = null

function clampDecimals(value: unknown, fallback: number): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  return Math.min(NUMBER_DECIMALS_MAX, Math.max(NUMBER_DECIMALS_MIN, Math.round(n)))
}

function normalizePrefs(raw: Partial<NumberDisplayFormatPrefs> | null | undefined): NumberDisplayFormatPrefs {
  const d = DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS
  return {
    vnd: clampDecimals(raw?.vnd, d.vnd),
    usd: clampDecimals(raw?.usd, d.usd),
    rmb: clampDecimals(raw?.rmb, d.rmb),
    general: clampDecimals(raw?.general, d.general),
    volume: clampDecimals(raw?.volume, d.volume),
    separator: raw?.separator === 'vi' ? 'vi' : 'en',
  }
}

function readFromStorage(): NumberDisplayFormatPrefs {
  if (typeof window === 'undefined') return { ...DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS }
  try {
    const raw = window.localStorage.getItem(NUMBER_DISPLAY_FORMAT_STORAGE_KEY)
    return normalizePrefs(raw ? JSON.parse(raw) : null)
  } catch {
    return { ...DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS }
  }
}

export function getNumberDisplayFormatPrefs(): NumberDisplayFormatPrefs {
  if (!cache) cache = readFromStorage()
  return cache
}

/** Đọc lại từ localStorage ở lần gọi tới (sau khi nhập file cài đặt). */
export function invalidateNumberDisplayFormatPrefsCache() {
  cache = null
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(NUMBER_DISPLAY_FORMAT_CHANGE_EVENT))
}

export function getNumberDisplayDecimals(key: NumberDecimalPrefKey): number {
  return getNumberDisplayFormatPrefs()[key]
}

export function getNumberSeparators(): { thousand: ',' | '.'; decimal: '.' | ',' } {
  return getNumberDisplayFormatPrefs().separator === 'vi'
    ? { thousand: '.', decimal: ',' }
    : { thousand: ',', decimal: '.' }
}

export function getNumberDisplayLocale(): string {
  return getNumberDisplayFormatPrefs().separator === 'vi' ? 'vi-VN' : 'en-US'
}

export function setNumberDisplayFormatPrefs(next: Partial<NumberDisplayFormatPrefs>): NumberDisplayFormatPrefs {
  const normalized = normalizePrefs({ ...getNumberDisplayFormatPrefs(), ...next })
  cache = normalized
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(NUMBER_DISPLAY_FORMAT_STORAGE_KEY, JSON.stringify(normalized))
    } catch {
      // private mode / quota
    }
    window.dispatchEvent(new Event(NUMBER_DISPLAY_FORMAT_CHANGE_EVENT))
  }
  return normalized
}

export function resetNumberDisplayFormatPrefs(): NumberDisplayFormatPrefs {
  return setNumberDisplayFormatPrefs({ ...DEFAULT_NUMBER_DISPLAY_FORMAT_PREFS })
}

/** Đổi cache tạm trong `fn` (không ghi localStorage) — preview trên màn cài đặt. */
export function withNumberDisplayFormatPrefsOverride<T>(next: NumberDisplayFormatPrefs, fn: () => T): T {
  const prev = cache
  cache = normalizePrefs(next)
  try {
    return fn()
  } finally {
    cache = prev
  }
}

/** Chuỗi người dùng đang gõ dở (chỉ dấu trừ / dấu thập phân cuối) — chưa commit số. */
export function isPartialNumberText(text: string): boolean {
  const { decimal } = getNumberSeparators()
  if (text === '-' || text === decimal || text === `-${decimal}`) return true
  return text.endsWith(decimal) && text.split(decimal).length <= 2
}

/** Đổi chuỗi theo dấu phân cách đang cài → number (`null` nếu không hợp lệ). */
export function parseNumberText(text: string): number | null {
  const { thousand, decimal } = getNumberSeparators()
  const cleaned = String(text ?? '')
    .replace(/\s/g, '')
    .split(thousand)
    .join('')
    .replace(decimal, '.')
  if (cleaned === '' || cleaned === '-') return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}
